import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

export const AdvancedDrilldownGrid = ({ data = [], loading,onCellClick }) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  /* ================= HEATMAP ================= */
  const getHeatColor = (diff) => {
    const value = Math.min(Math.abs(diff), 1000);
    const intensity = value / 10000;
    return `rgba(255,0,0,${intensity})`;
  };

  /* ================= COLUMN BUILD ================= */
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];

    return Object.keys(data[0]).map((key) => {
      const isSource = key.startsWith("s_");

      return {
        accessorKey: key,
        header: key.replace("s_", "Source ").replace("t_", "Target ").replace("c_", "Check "),
        cell: (info) => {
          const row = info.row.original;

          /* ================= DIFF VIEW ================= */
          if (isSource) {
            const targetKey = key.replace("s_", "t_");

            const sVal = row[key];
            const tVal = row[targetKey];

            const sNum = parseFloat(sVal);
            const tNum = parseFloat(tVal);

            const isNumeric = !isNaN(sNum) && !isNaN(tNum);
            const isDiff = sVal !== tVal;

            if (isNumeric) {
              const diff = sNum - tNum;

              return (
                <div
                  className="p-2 rounded"
                  style={{ background: getHeatColor(diff) }}
                >
                  <div className="text-red-600">Source: {sVal}</div>
                  <div className="text-green-600">Target: {tVal}</div>
                  <div className="text-xs text-gray-700">
                    Δ: {diff.toFixed(4)}
                  </div>
                </div>
              );
            }

            return (
              <div className="flex flex-col">
                <span className={isDiff ? "text-red-600" : ""}>
                  Source: {sVal}
                </span>
                <span className={isDiff ? "text-green-600" : ""}>
                  Target: {tVal}
                </span>
              </div>
            );
          }

          /* ================= CHECK COLUMN ================= */
          if (key.startsWith("c_")) {
            const value = row[key];

            return (
              <span
                className={
                  value === "FALSE"
                    ? "text-red-600 font-bold"
                    : "text-green-600"
                }
              >
                {value}
              </span>
            );
          }

          return info.getValue();
        },
      };
    });
  }, [data]);

  /* ================= TABLE ================= */
  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  /* ================= EXPORT ================= */
  const exportCSV = () => {
    if (!data.length) return;

    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) =>
      Object.values(row)
        .map((v) => `"${v}"`)
        .join(",")
    );

    const csv = [headers, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "drilldown.csv";
    a.click();
  };

  /* ================= UI ================= */
  if (loading) {
    return (
      <div className="bg-white p-6 rounded shadow text-center">
        Loading data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded shadow text-center text-gray-500">
        No records found
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-xl shadow">
      {/* 🔍 SEARCH + EXPORT */}
      <div className="flex justify-between mb-4">
        <input
          placeholder="Search anything..."
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="border px-3 py-2 rounded w-64"
        />

        <button
          onClick={exportCSV}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          Export CSV
        </button>
      </div>

      {/* 📊 TABLE */}
      <div className="overflow-auto max-h-[500px]">
        <table className="min-w-full text-sm border">
          <thead className="bg-gray-100 sticky top-0">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className="p-2 border cursor-pointer text-left"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted() === "asc" && " 🔼"}
                    {header.column.getIsSorted() === "desc" && " 🔽"}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="p-2 border align-top cursor-pointer"
                      onClick={() =>
                        onCellClick?.({
                          row: row.original,
                          column: cell.column.id,
                          value: cell.getValue(),
                        })
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
        </table>
      </div>
    </div>
  );
};